import type { CSSProperties, ReactNode } from "react";

/** THE THREE TIERS the Live Window is built from, and the colour discipline.
 *
 *  Dattatray: *"The focus is missing. There should be clear distinction
 *  between important and secondary information. Even the colour palette does
 *  not match Orbit's vibe."* All three complaints have one cause each, and
 *  they are worth naming because the fix is not "make it prettier".
 *
 *  ── WHY IT DID NOT LOOK LIKE ORBIT ─────────────────────────────────────
 *  The page painted its SECTIONS with STATUS tokens used as decoration:
 *  Device Info was `--info` (blue), Motor Info was `--warn` (amber), and
 *  every stat tile carried an accent underline drawn from the same four.
 *
 *  So a healthy motor section was AMBER — the warning colour — and nine
 *  identity rows were BLUE, the info colour. Orbit is teal-led (`--teal` is
 *  `--c-swift`, the brand value) with `--orange` second; teal was one of four
 *  equals here. A status colour used as decoration is an anti-pattern on its
 *  own terms, and it also spends the only channel the page has for saying
 *  something is wrong.
 *
 *  THE RULE NOW: one accent, teal, and it marks STRUCTURE. The status tokens
 *  (`--ok` / `--warn` / `--bad`) appear only where something is actually good
 *  or bad, and never alone — this console's `--ok` and `--warn` are ΔE 4.1
 *  apart under protanopia (measured), so every status colour ships with an
 *  icon and a word.
 *
 *  ── WHY THERE WAS NO FOCUS ─────────────────────────────────────────────
 *  Four cards of identical weight, each a grid of ~30 tiles at one size, all
 *  labels at one size. Nothing was emphasised, so nothing read as important.
 *  These components make the tier EXPLICIT and the type scale fixed, so a new
 *  field has to declare which tier it belongs to rather than defaulting into
 *  the loudest one.
 *
 *      Hero       34px   the one fact the page exists to answer
 *      Primary    24px   live, changing, decision-relevant
 *      Secondary  15px   live, supporting
 *      Reference  13px   static identity — true for months, read once
 *
 *  ── NUMBERS WEAR MONTSERRAT, IDENTIFIERS WEAR MONO ─────────────────────
 *  Every value on the page was `className="mono"`, which made a product look
 *  like a terminal dump. Mono is for things you compare CHARACTER BY
 *  CHARACTER — an IMEI, a hex word, a board ID. A speed is not one of those.
 *  Standalone figures also take PROPORTIONAL numerals: `tabular-nums` gives
 *  every digit the width of a `0`, which reads loose at display sizes and is
 *  reserved for columns (the record table uses it, correctly).
 */

const LABEL: CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 0.9,
  textTransform: "uppercase", color: "var(--t-mute)",
};

/** A card. ONE hairline, no coloured stripe.
 *
 *  `lead` turns the rule teal for the section that leads the page; everything
 *  else is structural neutral. That is the whole accent budget.
 */
export function Panel({ title, right, note, lead = false, faults = 0, children }: {
  title: string; right?: ReactNode; note?: string;
  lead?: boolean; faults?: number; children: ReactNode;
}) {
  return (
    <section className="panel" style={{ marginBottom: 16, overflow: "hidden" }}>
      <header style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        padding: "14px 20px 12px", borderBottom: "1px solid var(--line)",
      }}>
        <span aria-hidden style={{
          width: 3, height: 14, borderRadius: 2,
          background: lead ? "var(--teal)" : "var(--line2)",
        }} />
        <h3 style={{ ...LABEL, fontSize: 11, margin: 0, color: "var(--t-body)" }}>{title}</h3>
        {note && <span className="muted" style={{ fontSize: 11.5 }}>{note}</span>}
        {/* A FAULT IS STATUS, so it gets a status colour: and a word with it,
            because the colour alone is not legible to every reader. It rides
            the header because the registers themselves are chips at the
            BOTTOM of the card, under two rows of dials: the least visible
            thing in the card they belong to. */}
        {faults > 0 && (
          <span className="chip" title={`${faults} fault register${faults > 1 ? "s" : ""} in this section is reporting a non-zero value. See the chips below.`}
            style={{ color: "var(--bad)", background: "var(--bad-soft)", fontSize: 10.5, cursor: "help" }}>
            ⚠ {faults} fault{faults > 1 ? "s" : ""}
          </span>
        )}
        {right && <span style={{ marginLeft: "auto" }}>{right}</span>}
      </header>
      <div style={{ padding: "16px 20px 18px" }}>{children}</div>
    </section>
  );
}

/** TIER 1 — live, changing, and someone decides something from it.
 *
 *  `tone` is for a value whose MAGNITUDE is a status (an SoC of 8%), not for
 *  decoration. Left unset it stays in text ink, which is the default on
 *  purpose: a page where every number is coloured has told you nothing about
 *  which one to look at.
 */
export function Primary({ label, value, unit, tone, hint, sub }: {
  label: string; value: ReactNode; unit?: string;
  tone?: string; hint?: string; sub?: ReactNode;
}) {
  return (
    <div title={hint} style={{ minWidth: 0, cursor: hint ? "help" : undefined }}>
      <div style={LABEL}>{label}</div>
      <div style={{
        display: "flex", alignItems: "baseline", gap: 4, marginTop: 3,
        fontSize: 24, fontWeight: 700, lineHeight: 1.05, letterSpacing: -0.4,
        color: tone ?? "var(--t-bright)",
      }}>
        <span>{value}</span>
        {unit && <span style={{ fontSize: 12, fontWeight: 600, color: "var(--t-mute)", letterSpacing: 0 }}>{unit}</span>}
      </div>
      {sub && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/** TIER 2 — live and supporting. Read when the primary row prompts a question. */
export function Secondary({ label, value, unit, hint }: {
  label: string; value: ReactNode; unit?: string; hint?: string;
}) {
  return (
    <div title={hint} style={{ minWidth: 0, padding: "7px 0", borderTop: "1px solid var(--line)", cursor: hint ? "help" : undefined }}>
      <div style={{ ...LABEL, fontSize: 9.5, letterSpacing: 0.7 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 3, marginTop: 1 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: "var(--t-body)" }}>{value}</span>
        {unit && <span className="muted" style={{ fontSize: 10.5 }}>{unit}</span>}
      </div>
    </div>
  );
}

/** TIER 3 — static identity. An IMEI is the same next month; a board ID was
 *  decided at manufacture. These are looked up ONCE and then ignored, so they
 *  do not belong at the weight of a live reading, and they carried blue
 *  underlines that made nine of them the loudest block on the page. */
export function Reference({ label, value, mono = true }: {
  label: string; value: ReactNode; mono?: boolean;
}) {
  const absent = value == null || value === "";
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12,
                  fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid var(--line)" }}>
      <span className="muted" style={{ whiteSpace: "nowrap" }}>{label}</span>
      <span className={mono && !absent ? "mono" : undefined}
        style={{ color: absent ? "var(--t-dim)" : "var(--t-body)", fontWeight: 500,
                 textAlign: "right", overflow: "hidden", textOverflow: "ellipsis",
                 whiteSpace: "nowrap" }}>
        {absent ? "—" : value}
      </span>
    </div>
  );
}

/** Auto-fitting row for a tier. `min` sets how narrow a cell may get before
 *  the row wraps — the tiers want different values, and a shared `grid()`
 *  helper taking a pixel number was how every row ended up at 120px. */
export const row = (min: number, gap = "4px 28px"): CSSProperties => ({
  display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap,
});

/** A meter — a proportion, read at a glance, where a number alone would make
 *  you do the arithmetic. The track is a lighter step of the fill's own ramp
 *  so the state reads across the whole bar rather than only at its end. */
export function Meter({ frac, tone = "var(--teal)", height = 6 }: {
  frac: number | null; tone?: string; height?: number;
}) {
  const pct = frac == null ? 0 : Math.max(0, Math.min(1, frac)) * 100;
  return (
    <div style={{ height, borderRadius: height, background: "var(--well)", overflow: "hidden", marginTop: 6 }}>
      <div style={{ width: `${pct}%`, height: "100%", borderRadius: height,
                    background: tone, transition: "width .4s ease" }} />
    </div>
  );
}
